import { useEffect, useRef, useState } from "react";

interface VideoPlayerProps {
  url: string;
  type: "youtube" | "vimeo" | "embed" | "upload";
  thumbnail?: string;
  title?: string;
  className?: string;
  autoplay?: boolean;
}

export default function VideoPlayer({ url, type, thumbnail, title, className = "", autoplay = false }: VideoPlayerProps) {
  const [playing, setPlaying] = useState(autoplay);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Autoplay once the component mounts (matches the portfolio detail page requirement).
  useEffect(() => {
    setPlaying(true);
  }, []);

  const handlePlay = () => setPlaying(true);

  if (type === "upload") {
    return (
      <div className={`relative w-full rounded-2xl overflow-hidden bg-dark-card ${className}`}>
        <video
          ref={videoRef}
          src={url}
          controls
          poster={thumbnail}
          className="w-full h-full object-cover"
          title={title}
        />
      </div>
    );
  }

  // YouTube / Vimeo / generic embed
  if (playing) {
    let embedUrl = url;

    if (type === "youtube") {
      let videoId = "";
      if (url.includes("youtube.com/watch?v=")) {
        videoId = url.split("v=")[1].split("&")[0];
      } else if (url.includes("youtu.be/")) {
        videoId = url.split("youtu.be/")[1].split("?")[0];
      } else if (url.includes("youtube.com/shorts/")) {
        videoId = url.split("youtube.com/shorts/")[1].split("?")[0];
      }
      if (videoId) {
        embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1`;
      }
    } else if (type === "vimeo") {
      const match = url.match(/vimeo\.com\/(\d+)/);
      if (match) {
        embedUrl = `https://player.vimeo.com/video/${match[1]}?autoplay=1&muted=1`;
      }
    }

    return (
      <div className={`relative w-full rounded-2xl overflow-hidden bg-black ${className}`}>
        <iframe
          src={embedUrl}
          title={title || "Project Demo Video"}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          className="w-full h-full border-0"
          style={{ minHeight: "inherit" }}
        />
      </div>
    );
  }

  return (
    <div
      className={`relative w-full rounded-2xl overflow-hidden bg-dark-card cursor-pointer group ${className}`}
      onClick={handlePlay}
      role="button"
      aria-label={`Play ${title || "video"}`}
    >
      {/* Thumbnail */}
      {thumbnail ? (
        <img
          src={thumbnail}
          alt={title || "Video thumbnail"}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-dark-card to-deep-black flex items-center justify-center">
          <i className="ri-film-line text-6xl text-silver-dim" />
        </div>
      )}

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors duration-300" />

      {/* Play button */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative">
          {/* Glow ring */}
          <div className="absolute inset-0 rounded-full bg-electric-blue/20 blur-xl scale-150 group-hover:scale-175 transition-transform duration-300" />
          <div className="relative w-20 h-20 flex items-center justify-center rounded-full bg-electric-blue/90 group-hover:bg-electric-blue transition-colors duration-200 shadow-glow-blue">
            <i className="ri-play-fill text-3xl text-deep-black ml-1" />
          </div>
        </div>
      </div>

      {/* Label */}
      <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
        <span className="text-white text-sm font-medium bg-black/50 backdrop-blur-sm px-3 py-1 rounded-full">
          {title || "Watch Demo"}
        </span>
        <span className="text-silver-dim text-xs bg-black/50 backdrop-blur-sm px-3 py-1 rounded-full uppercase tracking-wider">
          {type === "youtube" ? "YouTube" : type === "vimeo" ? "Vimeo" : "Video"}
        </span>
      </div>
    </div>
  );
}
